import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Link as PDFLink, PDFDownloadLink, Text } from '@react-pdf/renderer';
import QR from 'qrcode';
import { cleanBookingFormData } from '../features/bookingForm/bookingFormSlice';
import {
    formatDateServerTz, formatTimeServerTz, pad, SERVER_TIMEZONE, toDate,
} from '../utils';
import googleButton from '../assets/images/googlePlay.svg';
import appleButton from '../assets/images/appStore.svg';
import { useWidgetProps } from '../WidgetPropsContext';

import './successBooking.scss';
import {
    getSelectedBranchDeeplink,
    getSelectedBranchName,
    getSelectedDoctorName,
    getSelectedDoctorSpecialization,
    getSelectedService,
    getServiceSelected,
} from '../serviceBooking/store/selectors';
import { setActiveTimeslotId, setPreselectedData } from '../serviceBooking/store/serviceSlice';
import { clearTimetableRequestParams, fetchTimeslots } from '../features/doctors/doctorsSlice';
import { selectFilters } from '../features/filters/selectors';
import { MMA_LINK, WEB_APP_URL } from '../clinicsData';
import { getSuccessBookingData } from '../features/bookingForm/selectors';
import { Ticket } from './Ticket';
import { TimeDiff } from '../serviceBooking/ServiceBookingForm/DoctorList/DoctorListItem/TimeslotList/TimesotList';
import { PUB_API_HOST } from '../config';

const LOCAL_PHONE_PREFIX = '375';
const button = require('b_')
    .with('button');

const successBooking = require('b_')
    .with('success-booking');
const text = require('b_')
    .with('text');

const DEEP_LINK_DEFAULT = `https://link.aibolit.md/l/mma`;

const isPhoneLocal = phone => phone?.startsWith?.(`+${LOCAL_PHONE_PREFIX}`) || phone?.startsWith?.(LOCAL_PHONE_PREFIX);

const getMmaLink = (baseUrl, phone, orderId, providerCode, allowDoctorBookmark) => {
    const link = `${MMA_LINK}?ac=login&pn=${phone ?? ''}&o=${orderId ?? ''}&pc=${
                providerCode ?? ''
            }${allowDoctorBookmark ? '&bm=1' : ''}`;
    const l = encodeURIComponent(link);
    return `${baseUrl}/orders/${orderId}/external-url?pn=%2B${phone}&r=${l}`;
};

// eslint-disable-next-line max-statements,complexity
export const SuccessBooking = () => {
    const dispatch = useDispatch();
    const {
        baseUrl,
        logo,
        allowDeepLink,
        linkToBumba,
        allowLinkToBumba,
        downloadPdf,
        providerCode,
        allowDoctorBookmark,
        allowMmaLink,
    } = useWidgetProps();

    const scrollRef = useRef();
    const { specialization } = useSelector(selectFilters);
    const successBookingData = useSelector(getSuccessBookingData);
    const {
        state: {
            doctorId,
            fullName: doctorFullName,
            specialityName: doctorSpecialization,
            timeslotId,
            freeTimeslots,
            branch: {
                branch: branchName,
                deepLink,
            },
            branch,
            patientData: {
                firstName,
                lastName,
                middleName,
                sex,
                phone,
            },
            patientData,
            ...rest
        },
    } = useLocation();

    const { serviceBooking } = useWidgetProps();
    const { serviceName } = useSelector(getSelectedService);
    const serviceDoctorName = useSelector(getSelectedDoctorName);
    const serviceDoctorSpec = useSelector(getSelectedDoctorSpecialization);
    const serviceBranchName = useSelector(getSelectedBranchName);
    const serviceDeepLink = useSelector(getSelectedBranchDeeplink);
    const {
        doctors,
        branches,
        services,
    } = useSelector(getServiceSelected);
    const reloadData = async () => {
        dispatch(cleanBookingFormData());
        dispatch(setActiveTimeslotId(null));
        if (serviceBooking) {
            dispatch(
                setPreselectedData({
                    doctors,
                    branches,
                    services,
                    timeslotsLoaded: false,
                    timetableLoaded: false,
                    reloadData: true,
                }),
            );
            // dispatch(selectService(services));
        } else {
            dispatch(
                fetchTimeslots({
                    branch: branch?.branchId,
                    date: rest.date,
                    specializationId: specialization,
                    baseUrl,
                }),
            );
            dispatch(clearTimetableRequestParams());
        }
    };

    const getPatient = useCallback(() => {
        return `Уважаем${sex === 'female' ? 'ая' : 'ый'} ${lastName} ${firstName} ${middleName}!`;
    }, [ lastName, firstName, middleName, sex ]);

    const getService = useCallback(() => {
        return serviceBooking ? serviceName : null;
    }, [ serviceBooking, serviceName ]);

    const getTimeslot = useCallback(
        (withTimezone = false) => {
            if (!withTimezone) {
                return successBookingData?.startDateTime && successBookingData?.endDateTime
                    ? `${formatDateServerTz(successBookingData.startDateTime)} в ${formatTimeServerTz(
                        successBookingData.startDateTime,
                    )}`
                    : 'Ошибка времени !!!';
            }
            return successBookingData?.startDateTime && successBookingData?.endDateTime ? (
                <span>
                    {`${formatDateServerTz(successBookingData.startDateTime)} в ${formatTimeServerTz(
                        successBookingData.startDateTime,
                    )}`}
                    <TimeDiff isBold={false}/>
                </span>
            ) : (
                'Ошибка времени !!!'
            );
        },
        [successBookingData],
    );

    const doctorName = useMemo(
        () => (serviceBooking ? serviceDoctorName : doctorFullName),
        [ serviceBooking, serviceDoctorName, doctorFullName ],
    );
    const doctorSpec = useMemo(
        () => (serviceBooking ? serviceDoctorSpec : doctorSpecialization),
        [ serviceBooking, serviceDoctorSpec, doctorSpecialization ],
    );
    const clinic = useMemo(
        () => (serviceBooking ? serviceBranchName : branchName),
        [ serviceBooking, serviceBranchName, branchName ],
    );
    const [ qrCode, setQrCode ] = useState('');
    const { clinicId } = useWidgetProps();

    useEffect(() => {
        const qrData = DEEP_LINK_DEFAULT;
        // const qrData = serviceBooking ? serviceDeepLink || DEEP_LINK_DEFAULT : deepLink || DEEP_LINK_DEFAULT;
        QR.toDataURL(qrData)
            .then((url) => {
                setQrCode(url);
            });
    }, [DEEP_LINK_DEFAULT]);

    const getLinkData = useCallback(
        (forWeb = false, old = false, onlyForLocal = false) => {
            if (onlyForLocal && !isPhoneLocal(phone)) {
                return {
                    text: '',
                    link: '',
                };
            }
            if (!isPhoneLocal(phone) || !allowDeepLink || old) {
                return {
                    text: 'Отменить или перенести текущий визит, а также создать новый можно в мобильном приложении Aibolit. Дополнительно доступны отзывы, история посещений, поиск лекарств и многое другое',
                    link: '',
                };
            }
            const l = `${WEB_APP_URL}?ac=login&pn=${phone ?? ''}&o=${successBookingData?.visitId ?? ''}&pc=${
                providerCode ?? ''
            }${allowDoctorBookmark ? '&bm=1' : ''}`;
            const t = forWeb ? (
                <>
                    <span>Ссылка для просмотра, отмены или переноса этого визита:&nbsp;</span>
                    <a
                        style={{
                            marginTop: 0,
                            textTransform: 'uppercase',
                        }}
                        href={l}
                        target={'_blank'}>
                        Открыть в Aibolit
                    </a>
                </>
            ) : (
                <Text>
                    Ссылка для просмотра, отмены или переноса этого визита:{' '}
                    <PDFLink src={l} style={{ textTransform: 'uppercase' }}>
                        Открыть в Aibolit
                    </PDFLink>
                </Text>
            );
            return {
                text: t,
                link: '',
            };
        },
        [ phone, successBookingData, clinicId, LOCAL_PHONE_PREFIX, allowDeepLink ],
    );

    const getBumbaLinkData = useCallback(
        (forWeb = false, old = false, onlyForLocal = false) => {
            if ((onlyForLocal && !isPhoneLocal(phone)) || !allowLinkToBumba || !linkToBumba || old) {
                return {
                    text: '',
                    link: '',
                };
            }
            const decodedLink = `${linkToBumba}?is-mcw-opened=true&ed=true&pn=${phone ?? ''}`;
            const l = encodeURIComponent(decodedLink);
            const safeLink = `https://${PUB_API_HOST}/api/v2/public/booking/${providerCode}/orders/${successBookingData?.visitId ?? ''}/external-url?pn=%2B${phone}&r=${l}`;
            const t = forWeb ? (
                <>
                    <span>Ссылка для просмотра или отмены этого визита:&nbsp;</span>
                    <a
                        style={{
                            marginTop: 0,
                            textTransform: 'uppercase',
                        }}
                        href={safeLink}
                        target={'_blank'}>
                        ЛИЧНЫЙ КАБИНЕТ
                    </a>
                </>
            ) : (
                <Text>
                    Ссылка для просмотра или отмены этого визита:{' '}
                    <PDFLink src={safeLink} style={{ textTransform: 'uppercase' }}>
                        ЛИЧНЫЙ КАБИНЕТ
                    </PDFLink>
                </Text>
            );
            return {
                text: t,
                link: '',
            };
        },
        [ phone, successBookingData, clinicId, LOCAL_PHONE_PREFIX, allowLinkToBumba, linkToBumba ],
    );

    const getMmaLinkData = useCallback(
        (forWeb = false, old = false, onlyForLocal = true) => {
            if ((onlyForLocal && !isPhoneLocal(phone)) || !allowMmaLink || old) {
                return {
                    text: '',
                    link: '',
                };
            }
            const link = getMmaLink(baseUrl, phone, successBookingData?.visitId ?? '', providerCode, allowDoctorBookmark);
            const t = forWeb ? (
                <>
                    <span>Ссылка для просмотра, отмены или переноса этого визита:&nbsp;</span>
                    <a
                        style={{
                            marginTop: 0,
                            textTransform: 'uppercase',
                        }}
                        href={link}
                        target={'_blank'}>
                        ОТКРЫТЬ В AIBOLIT
                    </a>
                </>
            ) : (
                <Text>
                    Ссылка для просмотра, отмены или переноса этого визита:{' '}
                    <PDFLink src={link} style={{ textTransform: 'uppercase' }}>
                        ОТКРЫТЬ В AIBOLIT
                    </PDFLink>
                </Text>
            );
            return {
                text: t,
                link: '',
            };
        },
        [ phone, successBookingData, clinicId, LOCAL_PHONE_PREFIX, allowMmaLink, baseUrl ],
    );

    useEffect(() => {
        scrollRef?.current?.scrollIntoView?.();
    }, [scrollRef]);

    const getFileName = useCallback(() => {
        if (!successBookingData?.startDateTime) {
            return 'visit.pdf';
        }
        try {
            const time = new Date(
                toDate(successBookingData?.startDateTime)
                    ?.toLocaleString('en-US', { timeZone: SERVER_TIMEZONE }),
            );
            const hours = pad(time.getHours());
            const minutes = pad(time.getMinutes());
            const year = time.getFullYear();
            const month = pad(time.getMonth() + 1);
            const day = pad(time.getDate());
            return `visit_${year}${month}${day}${hours}${minutes}.pdf`;
        } catch (e) {
            return 'visit.pdf';
        }
    }, [successBookingData?.startDateTime]);

    return (
        <div className={successBooking()} ref={scrollRef}>
            <div>
                <section className={successBooking('section')}>
                    {!!logo && <figure className={successBooking('logo-wrapper')}>
                        <img src={logo} className={successBooking('logo')} alt="clinics-logo"/>
                    </figure>}
                    <p className={text()}>{getPatient()}</p>
                    <h3 className={successBooking('title')}>Вы записаны на прием</h3>
                    <p className={text()}>{getTimeslot(true)}</p>
                    <p className={text()}>
                        {serviceBooking ? (
                            <strong>
                                {getService()}
                                <br/>
                            </strong>
                        ) : null}
                        <>
                            {doctorName}
                            <br/>
                            {doctorSpec}
                        </>
                        <br/>
                        {clinic}
                    </p>
                    {!!allowMmaLink && (
                        <p className={text()}>
                            <strong>{getMmaLinkData(true, false, true)?.text}</strong>
                        </p>
                    )}
                    {!!allowDeepLink && (
                        <p className={text()}>
                            <strong>{getLinkData(true, false, true)?.text}</strong>
                        </p>
                    )}
                    {!!allowLinkToBumba && (
                        <p className={text()}>
                            <strong>{getBumbaLinkData(true, false, true)?.text}</strong>
                        </p>
                    )}
                    <p className={text()}>
                        Будем рады вас видеть
                        <br/>С пожеланиями крепкого здоровья!
                    </p>

                    {!!downloadPdf && (
                        <div style={{ marginTop: 16 }}>
                            <PDFDownloadLink
                                document={
                                    <Ticket
                                        patient={getPatient()}
                                        logo={logo}
                                        service={getService()}
                                        timeslot={getTimeslot()}
                                        doctor={doctorName}
                                        specialization={doctorSpec}
                                        clinic={clinic}
                                        deepLink={qrCode}
                                        link={DEEP_LINK_DEFAULT}
                                        linkText="AIBOLIT"
                                        // link={serviceBooking ? serviceDeepLink || DEEP_LINK_DEFAULT : deepLink || DEEP_LINK_DEFAULT}
                                        cancelLink={getLinkData(false, false, true)}
                                        cancelLinkOld={getLinkData(undefined, true)}
                                        allowDeepLink={allowDeepLink}
                                        bumbaLink={getBumbaLinkData(false, false, true)}
                                        mmaLink={getMmaLinkData(false, false, true)}
                                    />
                                }
                                className={button('nohover')}
                                fileName={getFileName()}>
                                {({ loading }) => (loading ? 'Генерируем талон...' : 'Сохранить талон')}
                            </PDFDownloadLink>
                        </div>
                    )}
                    <Link
                        to={{
                            pathname: '/',
                            state: { patientData },
                        }}
                        onClick={reloadData}>
                        Оформить еще одну заявку
                    </Link>
                    <p className={text()}>
                        <strong>{getLinkData(true, true)?.text}</strong>
                    </p>
                    <p>Для установки приложения перейдите по ссылке или отсканируйте QR-код</p>
                    <a
                        href={getMmaLink(baseUrl, phone, successBookingData?.visitId ?? '', providerCode, allowDoctorBookmark)}
                        target="_blank">
                        AIBOLIT
                    </a>
                    <div
                        style={{
                            width: 100,
                            height: 100,
                        }}>
                        <QRCode
                            className={successBooking('qr-code')}
                            value={getMmaLink(baseUrl, phone, successBookingData?.visitId ?? '', providerCode, allowDoctorBookmark)}
                            size={100}></QRCode>
                    </div>
                    <a
                        target="_blank"
                        href={getMmaLink(baseUrl, phone, successBookingData?.visitId ?? '', providerCode, allowDoctorBookmark)}
                        className={successBooking('button', { google: true })}>
                        <img src={googleButton} alt="go to the google store"/>
                    </a>
                    <a
                        target="_blank"
                        href={getMmaLink(baseUrl, phone, successBookingData?.visitId ?? '', providerCode, allowDoctorBookmark)}
                        className={successBooking('button', { apple: true })}>
                        <img src={appleButton} alt="go to the apple store"/>
                    </a>
                </section>
            </div>
        </div>
    );
};
